import { Button, Icon, Position, Spinner } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { SearchParams, SearchPreset, SearchWithMetadata } from '@retro-ui/api-contract';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';

import cls from './SearchDetails.module.css';

interface SearchDetailsProps {
  search: SearchWithMetadata;
  tooltipPosition?: Position;
  triggerElement?: React.ReactNode;
  hideStartingMaterialDatasetsNames?: boolean;
}

export function SearchDetails(props: SearchDetailsProps) {
  const { search, tooltipPosition, triggerElement, hideStartingMaterialDatasetsNames } = props;

  const { startingMaterialDatasetData } = useSelector((state: any) => {
    const startingMaterialDatasetData = state.startingMaterial.dataset.data;

    return {
      startingMaterialDatasetData,
    };
  });

  const getDetailsSchema = () => {
    const info: Array<{
      label: string;
      content: React.ReactNode;
    }> = [];

    if (!isEmpty(startingMaterialDatasetData)) {
      const usedDatasets = (search.searchParams as SearchParams).presets.datasets ?? [];
      const updatedStartingMaterialInfo = Object.keys(startingMaterialDatasetData).map(id => {
        return {
          extendedId: startingMaterialDatasetData[id].extendedId,
          name: startingMaterialDatasetData[id].name,
        };
      });

      const usedDatasetsNames: string[] = [];
      usedDatasets.forEach(usedDataset => {
        const datasetInfo = updatedStartingMaterialInfo.find(e => e.extendedId === usedDataset.id);
        if (datasetInfo?.name) {
          usedDatasetsNames.push(datasetInfo.name);
        }
      });

      info.push({
        label: 'Starting materials datasets',
        content: (() => {
          if (hideStartingMaterialDatasetsNames) {
            return <span>{usedDatasets.map(el => el.id).join(', ')}</span>;
          } else if (usedDatasetsNames.length > 0) {
            return <span>{usedDatasetsNames.join(', ')}</span>;
          } else {
            return <b>None</b>;
          }
        })(),
      });
    }

    return info;
  };

  const params = search.searchParams as SearchParams;
  const canShowStartingMaterialsInfo = !isEmpty(startingMaterialDatasetData);
  const startingMaterialsInfo = canShowStartingMaterialsInfo ? getDetailsSchema() : [];

  return (
    <Tooltip2
      position={tooltipPosition ?? Position.BOTTOM}
      content={
        <div className={cls.tooltipContent}>
          <p className={cls.parametersListItem}>
            <span className={cls.parametersListLabel}>
              <Icon className={cls.toolbarIcon} icon="time" /> Created at
            </span>
            {new Date(search.createdAt).toLocaleString()}
          </p>
          <p className={cls.parametersListItem}>
            <span className={cls.parametersListLabel}>Credits due</span>
            {search.creditsDue != null ? search.creditsDue.toLocaleString() : 'n/a'}
          </p>
          <p className={cls.parametersListItem}>
            <span className={cls.parametersListLabel}>Source</span>
            {search.source}
          </p>
          <p className={cls.parametersListItem}>
            <span className={cls.parametersListLabel}>Parameter preset</span>
            {Object.keys(search?.inputParams as Partial<SearchPreset>).length === 0
              ? search.presetName
              : 'Custom'}
          </p>

          <h4 className={cls.parametersTitle}>Parameters</h4>
          <ul className={cls.parametersList}>
            <li className={cls.parametersListItem}>
              <span className={cls.parametersListLabel}>Model</span>
              <span>{params?.presets?.model}</span>
            </li>
            <li className={cls.parametersListItem}>
              <span className={cls.parametersListLabel}>Detail level</span>
              <span>{params?.detail_level}</span>
            </li>
            <li className={cls.parametersListItem}>
              <span className={cls.parametersListLabel}>Length</span>
              <span>{params?.presets?.length}</span>
            </li>
            <li className={cls.parametersListItem}>
              <span className={cls.parametersListLabel}>Synthesis scale</span>
              <span>{params?.presets?.synthesis_scale}</span>
            </li>
            {params?.presets?.reaction_confidence_threshold && (
              <li className={cls.parametersListItem}>
                <span className={cls.parametersListLabel}>Reaction confidence threshold</span>
                <span>{params.presets.reaction_confidence_threshold}</span>
              </li>
            )}
            {params?.presets?.min_reference_count !== undefined && (
              <li className={cls.parametersListItem}>
                <span className={cls.parametersListLabel}>Minimum reference count</span>
                <span>{params.presets.min_reference_count}</span>
              </li>
            )}

            {params?.presets?.min_n_providers !== undefined && (
              <li className={cls.parametersListItem}>
                <span className={cls.parametersListLabel}>SM minimum number of providers</span>
                <span>{params.presets.min_n_providers}</span>
              </li>
            )}
            {params?.presets?.max_n_atoms !== undefined && (
              <li className={cls.parametersListItem}>
                <span className={cls.parametersListLabel}>SM maximum number of atoms</span>
                <span>{params.presets.max_n_atoms}</span>
              </li>
            )}
            {params?.presets?.max_price !== undefined && (
              <li className={cls.parametersListItem}>
                <span className={cls.parametersListLabel}>SM maximum price</span>
                <span>${params.presets.max_price.toLocaleString('en-US')}</span>
              </li>
            )}
            {params?.presets?.max_price_per_gram !== undefined && (
              <li className={cls.parametersListItem}>
                <span className={cls.parametersListLabel}>SM maximum price per gram</span>
                <span>${params.presets.max_price_per_gram.toLocaleString('en-US')}</span>
              </li>
            )}
            {params?.presets?.excluded_smiles !== undefined && (
              <li className={cls.parametersListItem}>
                <span className={cls.parametersListLabel}>Excluded SMILES count</span>
                <span>{params.presets.excluded_smiles.length}</span>
              </li>
            )}

            {!canShowStartingMaterialsInfo ? (
              <li className={cls.parametersListItem}>
                <p className={cls.detailsLoading}>
                  <Spinner size={16} /> Loading more details...
                </p>
              </li>
            ) : (
              startingMaterialsInfo.map(info => (
                <li className={cls.parametersListItem} key={info.label}>
                  <span className={cls.parametersListLabel}>{info.label}</span>
                  <div>{info.content}</div>
                </li>
              ))
            )}
          </ul>
        </div>
      }
    >
      {triggerElement ?? <Button text="Details" icon="info-sign" minimal />}
    </Tooltip2>
  );
}
